import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/data/brand";

const NAV_ITEMS = [
  { to: "/the-firm", label: "The Firm" },
  { to: "/process", label: "Approach" },
  { to: "/claim-services", label: "Services" },
  { to: "/florida-coverage", label: "Coverage" },
  { to: "/insights", label: "Resources" },
  { to: "/contact", label: "Contact" },
  { to: "https://portal.claimwizard.com/login.jsp", label: "Customer Portal", external: true },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-midnight/85 backdrop-blur-2xl border-b border-white/5"
          : "bg-gradient-to-b from-midnight/70 to-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group" aria-label="AG Public Adjusting Home">
          <div className="relative flex items-center">
            <img
              src={BRAND.logoWhite}
              alt="AG Public Adjusting"
              className="h-9 sm:h-10 w-auto object-contain transition-transform duration-700 group-hover:scale-105"
              draggable={false}
            />
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ boxShadow: "0 0 24px rgba(0,119,255,0.5)" }} />
          </div>
          <div className="hidden sm:block leading-none whitespace-nowrap border-l border-white/10 pl-3">
            <div className="font-mono text-[0.55rem] text-gold/80 tracking-[0.32em] uppercase">Licensed · FL</div>
            <div className="font-mono text-[0.5rem] mt-1 text-ag-text-muted tracking-[0.3em] uppercase">Public Adjuster Firm</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-9">
          {NAV_ITEMS.map((item) => {
            if (item.external) {
              return (
                <a
                  key={item.to}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-link-customer-portal"
                  className="group font-display text-[0.78rem] tracking-[0.12em] uppercase font-light transition-colors duration-300 relative text-white/70 hover:text-gold flex items-center gap-1.5"
                >
                  <span>{item.label}</span>
                  <ArrowUpRight
                    className="w-3 h-3 text-gold/70 group-hover:text-gold transition-all duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    strokeWidth={1.6}
                  />
                </a>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.to.replace(/^\//, "").replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  `font-display text-[0.78rem] tracking-[0.12em] uppercase font-light transition-colors duration-300 relative ${
                    isActive ? "text-gold" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && <span className="absolute -bottom-1 left-0 right-0 h-px bg-gold" />}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden xl:flex items-center gap-4">
          <a href={BRAND.phoneHref} data-testid="nav-cta-call" className="font-mono text-[0.72rem] tracking-[0.15em] text-white/70 hover:text-gold transition-colors whitespace-nowrap flex items-center gap-2">
            <Phone className="w-3 h-3" />
            {BRAND.phoneDisplay}
          </a>
          <Link
            to="/free-claim-evaluation"
            data-testid="nav-cta-evaluation"
            className="btn-gold !py-2.5 !px-5 !text-[0.68rem] !tracking-[0.14em] whitespace-nowrap"
          >
            Free Evaluation
          </Link>
        </div>

        <button className="xl:hidden text-white p-2" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="nav-mobile-open">
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-midnight/98 backdrop-blur-2xl xl:hidden flex flex-col" data-testid="nav-mobile-drawer">
          <div className="flex items-center justify-between h-20 px-6">
            <Link to="/" className="flex items-center" onClick={() => setOpen(false)}>
              <img src={BRAND.logoWhite} alt="AG Public Adjusting" className="h-9 w-auto object-contain" />
            </Link>
            <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="nav-mobile-close" className="text-white p-2">
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex-1 flex flex-col px-8 pt-8 gap-5 sm:gap-7 overflow-y-auto">
            {NAV_ITEMS.map((item) => {
              if (item.external) {
                return (
                  <a
                    key={item.to}
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-link-customer-portal"
                    className="group font-display font-light text-[2rem] sm:text-4xl text-white/90 hover:text-gold transition-colors tracking-[-0.025em] inline-flex items-center gap-3"
                  >
                    <span>{item.label}</span>
                    <ArrowUpRight
                      className="w-5 h-5 sm:w-6 sm:h-6 text-gold/70 group-hover:text-gold transition-all duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      strokeWidth={1.4}
                    />
                  </a>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${item.to.replace(/^\//, "")}`}
                  className="font-display font-light text-[2rem] sm:text-4xl text-white/90 hover:text-gold transition-colors tracking-[-0.025em]"
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="p-6 sm:p-8 flex flex-col gap-3 border-t border-white/8" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)" }}>
            <a href={BRAND.phoneHref} className="btn-ghost-blue flex items-center justify-center gap-2" data-testid="mobile-cta-call">
              <Phone className="w-4 h-4" /> {BRAND.phoneDisplay}
            </a>
            <Link to="/free-claim-evaluation" onClick={() => setOpen(false)} data-testid="mobile-cta-evaluation" className="btn-gold text-center">
              Free Evaluation
            </Link>
            <div className="mt-2 font-mono text-[0.55rem] text-ag-text-muted tracking-[0.32em] uppercase text-center">
              {BRAND.licenseNumber}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
